import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';

export interface TabItem {
  id: string;
  path: string;
  title: string;
  icon?: string;
  pinned?: boolean;
}

export interface PendingTab {
  path: string;
  title: string;
  icon?: string;
}

interface TabContextType {
  tabs: TabItem[];
  activeTabId: string;
  openTab: (path: string, title: string, icon?: string) => void;
  updateTabTitle: (path: string, title: string) => void;
  closeTab: (id: string) => void;
  switchTab: (id: string) => void;
  closeOtherTabs: (id: string) => void;
  closeAllTabs: () => void;
  togglePinTab: (id: string) => void;
  clearUnpinnedTabs: () => void;
  isTabSystemActive: boolean;
  // Tab limit modal state
  tabLimitReached: boolean;
  pendingTab: PendingTab | null;
  confirmOpenTab: (tabIdsToClose: string[]) => void;
  cancelOpenTab: () => void;
}

const STORAGE_KEY = 'gts_open_tabs';
const ACTIVE_TAB_KEY = 'gts_active_tab';
const MAX_TABS = 15;

const DEFAULT_TAB: TabItem = {
  id: 'tab-accounting-dashboard',
  path: '/accounting/dashboard',
  title: 'Yönetici Ekranı',
  pinned: true,
};

function generateTabId(path: string): string {
  return 'tab-' + path.replace(/\//g, '-').replace(/^-/, '');
}

function loadTabs(): TabItem[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as TabItem[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        const hasDefault = parsed.some(t => t.path === DEFAULT_TAB.path);
        if (!hasDefault) {
          return [DEFAULT_TAB, ...parsed];
        }
        return parsed.map(t => t.path === DEFAULT_TAB.path ? { ...t, pinned: true } : t);
      }
    }
  } catch { /* ignore */ }
  return [DEFAULT_TAB];
}

function loadActiveTabId(tabs: TabItem[]): string {
  try {
    const stored = localStorage.getItem(ACTIVE_TAB_KEY);
    if (stored && tabs.some(t => t.id === stored)) {
      return stored;
    }
  } catch { /* ignore */ }
  return DEFAULT_TAB.id;
}

const TabContext = createContext<TabContextType | null>(null);

export const useTabContext = (): TabContextType => {
  const ctx = useContext(TabContext);
  if (!ctx) {
    throw new Error('useTabContext must be used within a TabProvider');
  }
  return ctx;
};

export const useTabContextSafe = (): TabContextType | null => {
  return useContext(TabContext);
};

interface TabProviderProps {
  children: ReactNode;
}

export const TabProvider: React.FC<TabProviderProps> = ({ children }) => {
  const [tabs, setTabs] = useState<TabItem[]>(() => loadTabs());
  const [activeTabId, setActiveTabId] = useState<string>(() => loadActiveTabId(loadTabs()));
  const [isTabSystemActive] = useState<boolean>(() => {
    return window.innerWidth >= 1000;
  });
  const [tabLimitReached, setTabLimitReached] = useState(false);
  const [pendingTab, setPendingTab] = useState<PendingTab | null>(null);

  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
    } catch { /* ignore */ }
  }, [tabs]);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, activeTabId);
    } catch { /* ignore */ }
  }, [activeTabId]);

  const openTab = useCallback((path: string, title: string, icon?: string) => {
    const existingTab = tabsRef.current.find(t => t.path === path);
    if (existingTab) {
      if (existingTab.title !== title || existingTab.icon !== icon) {
        setTabs(prev =>
          prev.map(tab =>
            tab.path === path ? { ...tab, title, icon: icon ?? tab.icon } : tab
          )
        );
      }
      setActiveTabId(existingTab.id);
      return;
    }

    if (tabsRef.current.length >= MAX_TABS) {
      setPendingTab({ path, title, icon });
      setTabLimitReached(true);
      return;
    }

    const newTab: TabItem = {
      id: generateTabId(path),
      path,
      title,
      icon,
    };

    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTab.id);
  }, []);

  const updateTabTitle = useCallback((path: string, title: string) => {
    setTabs(prev =>
      prev.map(tab =>
        tab.path === path && tab.title !== title ? { ...tab, title } : tab
      )
    );
  }, []);

  const confirmOpenTab = useCallback((tabIdsToClose: string[]) => {
    if (!pendingTab) return;

    const newTab: TabItem = {
      id: generateTabId(pendingTab.path),
      path: pendingTab.path,
      title: pendingTab.title,
      icon: pendingTab.icon,
    };

    setTabs(prev => {
      const filtered = prev.filter(t => !tabIdsToClose.includes(t.id) || t.pinned);
      return [...filtered, newTab];
    });
    setActiveTabId(newTab.id);
    setPendingTab(null);
    setTabLimitReached(false);
  }, [pendingTab]);

  const cancelOpenTab = useCallback(() => {
    setPendingTab(null);
    setTabLimitReached(false);
  }, []);

  const closeTab = useCallback((id: string) => {
    setTabs(prev => {
      const tab = prev.find(t => t.id === id);
      if (!tab || tab.pinned) return prev;

      const idx = prev.findIndex(t => t.id === id);
      const next = prev.filter(t => t.id !== id);

      if (id === tabsRef.current.find(t => t.id === activeTabId)?.id) {
        const newActiveIdx = Math.min(idx, next.length - 1);
        const newActive = next[newActiveIdx] || next[0];
        if (newActive) {
          setActiveTabId(newActive.id);
        }
      }

      return next;
    });
  }, [activeTabId]);

  const switchTab = useCallback((id: string) => {
    setActiveTabId(id);
  }, []);

  const closeOtherTabs = useCallback((id: string) => {
    setTabs(prev => prev.filter(t => t.id === id || t.pinned));
    setActiveTabId(id);
  }, []);

  const closeAllTabs = useCallback(() => {
    setTabs(prev => prev.filter(t => t.pinned));
    setActiveTabId(DEFAULT_TAB.id);
  }, []);

  const togglePinTab = useCallback((id: string) => {
    setTabs(prev => prev.map(t => {
      if (t.id !== id) return t;
      if (t.path === DEFAULT_TAB.path) return t;
      return { ...t, pinned: !t.pinned };
    }));
  }, []);

  const clearUnpinnedTabs = useCallback(() => {
    const pinnedOnly = tabsRef.current.filter(t => t.pinned);
    setTabs(pinnedOnly);
    setActiveTabId(DEFAULT_TAB.id);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pinnedOnly));
      localStorage.setItem(ACTIVE_TAB_KEY, DEFAULT_TAB.id);
    } catch { /* ignore */ }
  }, []);

  return (
    <TabContext.Provider value={{
      tabs,
      activeTabId,
      openTab,
      updateTabTitle,
      closeTab,
      switchTab,
      closeOtherTabs,
      closeAllTabs,
      togglePinTab,
      clearUnpinnedTabs,
      isTabSystemActive,
      tabLimitReached,
      pendingTab,
      confirmOpenTab,
      cancelOpenTab,
    }}>
      {children}
    </TabContext.Provider>
  );
};
